import React, { useEffect, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { useExperienceOffersByFilters, useExperienceOffersByPlaceIds } from '../../hooks';
import { useDeleteAllTagAssignments, useDeleteTagAssignment } from '../../hooks/useTagAssignments';
import { useTagAssignmentsForForm } from '../../hooks/useTagAssignmentsForForm';
import TagAssignmentsDestinationSearch from '../TagAssignmentsDestination/TagAssignmentsDestinationSearch';

import TagAssignmentsExperienceList from './TagAssignmentsExperienceList';

enum SearchModeOptions {
  ID = 'idIn',
  TITLE = 'title',
  VENDOR = 'vendor',
  LOCATION = 'location',
}

const searchModeOptionsLabels = {
  [SearchModeOptions.ID]: 'ID',
  [SearchModeOptions.TITLE]: 'Title',
  [SearchModeOptions.VENDOR]: 'Vendor',
  [SearchModeOptions.LOCATION]: 'Location',
};

interface Props {
  tagId?: string;
  selectedOfferIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
  savedOfferIds?: Array<string>;
}

const AccordionStyled = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&:not(:last-child)': {
    marginBottom: theme.spacing(1),
  },
}));

const AccordionSummaryStyled = styled(AccordionSummary)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

const AccordionDetailsStyled = styled(AccordionDetails)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

export default function TagAssignmentsExperience({
  tagId,
  selectedOfferIds,
  onSelectionChange,
  savedOfferIds = [],
}: Props) {
  const [placeIds, setPlaceIds] = useState<Array<string>>([]);
  const [savedExpanded, setSavedExpanded] = useState(true);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [searchModeSelected, setSearchModeSelected] = useState<SearchModeOptions>(SearchModeOptions.LOCATION);
  const [fieldSearchValue, setFieldSearchValue] = useState('');
  const [debouncedFieldSearchValue, setDebouncedFieldSearchValue] = useState('');
  const { enqueueSnackbar } = useSnackbar();

  const { paginationModel: savedPaginationModel, setPaginationModel: setSavedPaginationModel } = useDataGridPagination({
    pageSize: 10,
    page: 0,
  });

  const { savedAssignments, isLoading: isLoadingAssignments } = useTagAssignmentsForForm({
    tagId,
  });

  const deleteTagAssignment = useDeleteTagAssignment();
  const deleteAllTagAssignments = useDeleteAllTagAssignments();

  const experienceAssignments = savedAssignments.filter((a) => a.entityType === 'EXPERIENCE');

  const handleDeleteClick = (assignmentId: string) => {
    if (savedAssignments.length === 1) {
      enqueueSnackbar('Cannot delete the unique assignment. A tag must have at least one assignment.', {
        variant: 'error',
      });
      return;
    }
    setAssignmentToDelete(assignmentId);
    setDeleteDialogOpen(true);
  };

  const savedAssignmentsColumns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'entityId',
        headerName: 'Entity ID',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          const isDeleting = deleteTagAssignment.isPending && assignmentToDelete === params.row.id;
          return (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row.id)}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteTagAssignment.isPending, assignmentToDelete],
  );

  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) {
      return;
    }
    await deleteTagAssignment.mutateAsync(assignmentToDelete);
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteAllClick = () => {
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    const assignmentIds = experienceAssignments.map((a) => a.id);
    await deleteAllTagAssignments.mutateAsync(assignmentIds);
    setDeleteAllDialogOpen(false);
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  const handleDestinationsChange = (newPlaceIds: Array<string>) => {
    setPlaceIds(newPlaceIds);
  };

  const handleDestinationRemove = (placeIdToRemove: string) => {
    const offerIdsToRemove = offerIdsByPlaceId[placeIdToRemove] || [];
    const savedOfferIdsSet = new Set(savedOfferIds);

    if (offerIdsToRemove.length > 0) {
      const offerIdsToRemoveSet = new Set(offerIdsToRemove);
      const updatedSelectedIds = selectedOfferIds.filter(
        (id) => !offerIdsToRemoveSet.has(id) || savedOfferIdsSet.has(id),
      );
      onSelectionChange(updatedSelectedIds);
    }

    setPlaceIds((prev) => prev.filter((id) => id !== placeIdToRemove));
  };

  const handleSearchModeChange = (event: SelectChangeEvent<SearchModeOptions>) => {
    setSearchModeSelected(event.target.value as SearchModeOptions);
    setFieldSearchValue('');
    setPlaceIds([]);
  };

  const handleSearchFieldValue = (value: string) => {
    setFieldSearchValue(value);
  };

  const debouncedSetSearchValue = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedFieldSearchValue(value);
      }, 500),
    [],
  );

  useEffect(() => {
    debouncedSetSearchValue(fieldSearchValue);
    return () => {
      debouncedSetSearchValue.cancel();
    };
  }, [fieldSearchValue, debouncedSetSearchValue]);

  const isLocationMode = searchModeSelected === SearchModeOptions.LOCATION;
  const hasSearchValue = debouncedFieldSearchValue.trim().length > 0;
  const shouldSearchByFilters =
    !isLocationMode &&
    hasSearchValue &&
    (searchModeSelected === SearchModeOptions.ID || debouncedFieldSearchValue.length >= 2);

  const filters = useMemo(() => {
    if (!shouldSearchByFilters) {
      return {};
    }
    const filterObj: { idIn?: string | null; title?: string | null; vendor?: string | null } = {};
    if (searchModeSelected === SearchModeOptions.ID) {
      filterObj.idIn = debouncedFieldSearchValue.trim();
    } else if (searchModeSelected === SearchModeOptions.TITLE) {
      filterObj.title = debouncedFieldSearchValue.trim();
    } else if (searchModeSelected === SearchModeOptions.VENDOR) {
      filterObj.vendor = debouncedFieldSearchValue.trim();
    }
    return filterObj;
  }, [shouldSearchByFilters, searchModeSelected, debouncedFieldSearchValue]);

  const {
    offers: experienceOffersByPlaceIds,
    isLoading: isLoadingExperiencesByPlaceIds,
    offerIdsByPlaceId,
  } = useExperienceOffersByPlaceIds({
    placeIds,
    enabled: isLocationMode && placeIds.length > 0 && searchExpanded,
  });

  const { offers: experienceOffersByFilters, isLoading: isLoadingExperiencesByFilters } = useExperienceOffersByFilters({
    filters,
    enabled: shouldSearchByFilters && searchExpanded,
  });

  const experienceOffers = isLocationMode ? experienceOffersByPlaceIds : experienceOffersByFilters;
  const isLoadingExperiences = isLocationMode ? isLoadingExperiencesByPlaceIds : isLoadingExperiencesByFilters;
  const shouldShowResults = isLocationMode ? placeIds.length > 0 : shouldSearchByFilters;

  useEffect(() => {
    if (searchModeSelected !== SearchModeOptions.LOCATION) {
      setPlaceIds([]);
    }
    setFieldSearchValue('');
    setDebouncedFieldSearchValue('');
  }, [searchModeSelected]);

  return (
    <Box>
      {tagId && (
        <AccordionStyled expanded={savedExpanded} onChange={(_, expanded) => setSavedExpanded(expanded)}>
          <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">
              Saved Assignments ({experienceAssignments.length})
            </Typography>
          </AccordionSummaryStyled>
          <AccordionDetailsStyled>
            <Box>
              {isLoadingAssignments ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <CircularProgress />
                </Box>
              ) : experienceAssignments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No saved experience assignments
                </Typography>
              ) : (
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeleteAllClick}
                      disabled={deleteAllTagAssignments.isPending}
                    >
                      Delete All
                    </Button>
                  </Box>
                  <DataGrid
                    rows={experienceAssignments}
                    columns={savedAssignmentsColumns}
                    getRowId={(row) => row.id}
                    pagination
                    paginationModel={savedPaginationModel}
                    onPaginationModelChange={setSavedPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    autoHeight
                    disableRowSelectionOnClick
                    disableColumnMenu
                  />
                </Stack>
              )}
            </Box>
          </AccordionDetailsStyled>
        </AccordionStyled>
      )}
      <AccordionStyled expanded={searchExpanded} onChange={(_, expanded) => setSearchExpanded(expanded)}>
        <AccordionSummaryStyled expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Add New Assignments
          </Typography>
        </AccordionSummaryStyled>
        <AccordionDetailsStyled>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box minWidth={150}>
                <Select value={searchModeSelected} onChange={handleSearchModeChange} size="small" fullWidth>
                  {Object.entries(searchModeOptionsLabels).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Box flexGrow={1}>
                {isLocationMode ? (
                  <TagAssignmentsDestinationSearch
                    onSelectDestinations={handleDestinationsChange}
                    onRemoveDestination={handleDestinationRemove}
                    savedPlaceIds={[]}
                    selectedPlaceIds={placeIds}
                  />
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    value={fieldSearchValue}
                    onChange={(e) => handleSearchFieldValue(e.target.value)}
                    autoComplete="off"
                    placeholder={
                      searchModeSelected === SearchModeOptions.ID
                        ? 'Search experiences by ID separated by comma'
                        : searchModeSelected === SearchModeOptions.TITLE
                        ? 'Search experiences by title'
                        : 'Search experiences by vendor'
                    }
                  />
                )}
              </Box>
            </Stack>
            {shouldShowResults && (
              <TagAssignmentsExperienceList
                offers={experienceOffers}
                selectedOfferIds={selectedOfferIds}
                onSelectionChange={onSelectionChange}
                isLoading={isLoadingExperiences}
                savedOfferIds={savedOfferIds}
              />
            )}
          </Stack>
        </AccordionDetailsStyled>
      </AccordionStyled>
      <Dialog open={deleteDialogOpen} onClose={deleteTagAssignment.isPending ? undefined : handleDeleteCancel}>
        <DialogTitle>Delete Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this assignment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="text" disabled={deleteTagAssignment.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteTagAssignment.isPending}
            startIcon={deleteTagAssignment.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteAllDialogOpen}
        onClose={deleteAllTagAssignments.isPending ? undefined : handleDeleteAllCancel}
      >
        <DialogTitle>Delete All Experience Assignments</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all {experienceAssignments.length} experience assignment(s)? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteAllCancel} variant="text" disabled={deleteAllTagAssignments.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteAllTagAssignments.isPending}
            startIcon={deleteAllTagAssignments.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
